import type { AgentCodeFix } from "@productlane/zero/queries"
import type { AgentRecommendation } from "@productlane/zero/schema"

import type { SuggestionRow } from "../helpCenter/aggregate-proposals"
import type { AgentSetupStep } from "./agent-setup"
import { isApiRow } from "../helpCenter/aggregate-proposals"
import { setupItemId } from "./agent-setup"

export type CodeFixState = "review" | "running"

export interface AgentRecommendationItem {
	kind: "recommendation"
	id: string
	type: AgentRecommendation["type"]
	recommendation: AgentRecommendation
	articleIcon?: string
}

export interface AgentCodeFixItem {
	kind: "code"
	id: string
	type: "CODE"
	fix: AgentCodeFix
	state: CodeFixState
}

// The fields the list needs from an open doc suggestion group.
export interface AgentDocsGroup {
	id: string
	title?: string | null
	source: string
	authorType: "USER" | "AGENT"
	createdAt: number
	proposals?: readonly {
		proposedIcon?: string | null
		article?: { icon?: string | null } | null
	}[]
	// The conversations a thread-sourced suggestion was written from.
	sourceThreads?: readonly unknown[]
}

/** The icon of the article a docs change touches: the one it proposes, else the article's own. */
export function docsGroupIcon(group: AgentDocsGroup): string | undefined {
	for (const proposal of group.proposals ?? []) {
		const icon = proposal.proposedIcon || proposal.article?.icon
		if (icon) return icon
	}
	return undefined
}

export interface AgentDocsItem {
	kind: "docs"
	id: string
	type: "DOCS_SUGGESTION"
	group: AgentDocsGroup
	articleIcon?: string
}

// A docs change outside any group: a teammate's suggested edit, or an API draft.
export interface AgentProposalItem {
	kind: "proposal"
	id: string
	type: "DOCS_SUGGESTION"
	row: SuggestionRow
	articleIcon?: string
}

/** Route-safe id for an ungrouped docs row, whose key can be "new:<proposalId>". */
export function proposalItemId(row: Pick<SuggestionRow, "key">): string {
	return `proposal-${row.key.replace(":", "-")}`
}

export function proposalRowTime(row: Pick<SuggestionRow, "proposals">): number {
	return Math.max(0, ...row.proposals.map((p) => p.createdAt))
}

// A basic the workspace has not set up yet, shown until it is done.
export interface AgentSetupItem {
	kind: "setup"
	id: string
	type: "SETUP"
	step: AgentSetupStep
}

export type AgentItem =
	| AgentSetupItem
	| AgentRecommendationItem
	| AgentCodeFixItem
	| AgentDocsItem
	| AgentProposalItem

const LISTED_RECOMMENDATION_TYPES = new Set<AgentRecommendation["type"]>([
	"CONTENT",
	"CONTRADICTION",
	"GUIDANCE",
	"DATA",
	"ACTION",
])

// A fix session times out at 45 minutes and verify runs after it. A run still
// marked running long past that lost its Inngest run, so it leaves the page.
export const STALE_RUN_MS = 2 * 60 * 60 * 1000

export function codeFixState(
	fix: Pick<
		AgentCodeFix,
		"reviewState" | "prNumber" | "status" | "statusChangedAt"
	>,
	now: number,
): CodeFixState | null {
	if (fix.reviewState === "PENDING" && fix.prNumber) return "review"
	if (fix.status === "RUNNING" && now - fix.statusChangedAt < STALE_RUN_MS) {
		return "running"
	}
	return null
}

// Open setup steps first, then code fixes awaiting review, sessions still
// working, recommendations by impact, and the other doc suggestions, newest first.
export function buildAgentItems({
	setup = [],
	recommendations,
	fixes,
	docsGroups = [],
	docsLoaded = false,
	proposalRows = [],
	now,
}: {
	setup?: readonly AgentSetupStep[]
	recommendations: readonly AgentRecommendation[]
	fixes: readonly AgentCodeFix[]
	docsGroups?: readonly AgentDocsGroup[]
	// True once docsGroups holds every open group, so a missing one is closed.
	docsLoaded?: boolean
	proposalRows?: readonly SuggestionRow[]
	now: number
}): AgentItem[] {
	const reviews: AgentCodeFixItem[] = []
	const running: AgentCodeFixItem[] = []
	for (const fix of fixes) {
		const state = codeFixState(fix, now)
		if (!state) continue
		const item: AgentCodeFixItem = {
			kind: "code",
			id: fix.id,
			type: "CODE",
			fix,
			state,
		}
		if (state === "review") reviews.push(item)
		else running.push(item)
	}
	const openGroupIds = new Set(docsGroups.map((group) => group.id))
	// A card whose docs change closed elsewhere (folded into another suggestion,
	// expired) stays OPEN until the next review run syncs it.
	const listed = recommendations.filter(
		(r) =>
			LISTED_RECOMMENDATION_TYPES.has(r.type) &&
			!(
				docsLoaded &&
				r.proposalGroupId &&
				!openGroupIds.has(r.proposalGroupId)
			),
	)
	// A group behind a recommendation shows once, as the recommendation.
	const owned = new Set(
		listed.map((r) => r.proposalGroupId).filter((id): id is string => !!id),
	)
	const iconByGroup = new Map(
		docsGroups.map((group) => [group.id, docsGroupIcon(group)]),
	)
	const docs: (AgentDocsItem | AgentProposalItem)[] = [
		...docsGroups
			.filter((group) => !owned.has(group.id))
			.map((group): AgentDocsItem => ({
				kind: "docs",
				id: group.id,
				type: "DOCS_SUGGESTION",
				group,
				articleIcon: iconByGroup.get(group.id),
			})),
		// API and MCP drafts are reviewed on the Docs page.
		...proposalRows
			.filter((row) => !isApiRow(row))
			.map((row): AgentProposalItem => ({
				kind: "proposal",
				id: proposalItemId(row),
				type: "DOCS_SUGGESTION",
				row,
				articleIcon:
					row.proposals.find((p) => p.proposedIcon)?.proposedIcon ?? undefined,
			})),
	].sort((a, b) => docsItemTime(b) - docsItemTime(a))
	return [
		...setup.map((step): AgentSetupItem => ({
			kind: "setup",
			id: setupItemId(step.id),
			type: "SETUP",
			step,
		})),
		...reviews,
		...running,
		...listed.map((recommendation): AgentRecommendationItem => ({
			kind: "recommendation",
			id: recommendation.id,
			type: recommendation.type,
			recommendation,
			articleIcon: recommendation.proposalGroupId
				? iconByGroup.get(recommendation.proposalGroupId)
				: undefined,
		})),
		...docs,
	]
}

function docsItemTime(item: AgentDocsItem | AgentProposalItem): number {
	return item.kind === "docs" ? item.group.createdAt : proposalRowTime(item.row)
}

// Items a teammate can act on, for the sidebar count. A running session is
// shown but waits on nobody.
export function actionableCount(items: readonly AgentItem[]): number {
	return items.filter((item) => item.kind !== "code" || item.state === "review")
		.length
}

export interface CodeFixLinks {
	pullRequest: string | null
	issue: string | null
	thread: string | null
}

export function codeFixLinks(
	fix: Pick<AgentCodeFix, "prUrl" | "issue" | "feedbackId">,
): CodeFixLinks {
	return {
		pullRequest: fix.prUrl || null,
		issue: fix.issue?.url || null,
		thread: fix.feedbackId ? `/threads/${fix.feedbackId}` : null,
	}
}

export function codeFixTitle(
	fix: Pick<AgentCodeFix, "summary" | "prNumber" | "issue" | "feedback">,
): string {
	return (
		fix.summary?.trim() ||
		fix.issue?.title.trim() ||
		fix.feedback?.title?.trim() ||
		(fix.prNumber ? `Pull request #${fix.prNumber}` : "Bug report")
	)
}
